import {
  EmptyStateBase,
  useModals,
  Button,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Skeleton,
  Stack,
  Title,
} from "@skailer-com/uikit";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { GoalCard } from "@/pages/talent/goals/components/GoalCard.tsx";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { GoalEditModal } from "@/pages/talent/goals/widgets/GoalEditModal.tsx";
import { useEmployeeGoalsSearch } from "@/queries/employees/use-employee-goals.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiGoal, ApiGoalShort, ApiGoalStatus } from "@/types/goals.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";
import { useSearchParams } from "react-router-dom";

export function EmployeeGoals({ employee }: EmployeePageComponentProps) {
  const { t } = useTranslation();
  const modalsService = useModals();
  const { canEditField } = usePermissions();
  const [, setSearchParams] = useSearchParams();

  const handleGoalClick = (goal: ApiGoal | ApiGoalShort) => {
    setSearchParams({ sideGoal: goal.id.toString() });
  };

  const handleCreateGoal = () => {
    modalsService.openModal(GoalEditModal, {
      owner_employee: employee,
      disableOwnerTypeChoice: true,
    });
  };

  const [showAll, setShowAll] = useState(false);

  const { data: goalsData, isLoading } = useEmployeeGoalsSearch(employee.id, {
    status: showAll ? [] : [ApiGoalStatus.active],
  });
  const goals = goalsData?.items ?? [];

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.goals.employeeListShown, {
      [analyticProperties.id]: employee.id,
    });
  });

  const publicGoals = useMemo(() => goals.filter((g) => g.is_shared), [goals]);
  const privateGoals = useMemo(() => goals.filter((g) => !g.is_shared), [goals]);

  const renderGoalsSection = (sectionGoals: ApiGoal[], title: string, icon?: string) => {
    if (sectionGoals.length === 0) return null;
    return (
      <Stack>
        <Stack direction="horizontal" gap="xs" items="center" className={"pb-2"}>
          <Title
            size={"xs"}
            headerIcon={icon && <FontAwesomeIcon icon={icon} className="text-secondary" />}
            header={title}
            paddingTop
          />
        </Stack>
        <Stack gap="sm">
          {sectionGoals.map((goal) => (
            <GoalCard key={goal.id} goal={goal} onClick={handleGoalClick} />
          ))}
        </Stack>
      </Stack>
    );
  };

  if (isLoading) {
    return (
      <Stack gap="sm">
        <Stack direction="horizontal" justify="between">
          <Title size="lg" header={t("goals:my_goals")} paddingBottom />
        </Stack>
        <Stack gap="sm">
          {Array.from({ length: 3 }).map((_, i) => (
            <Stack key={i} className="bg-dark/5 border border-dark/5 rounded-xs px-4 py-5">
              <Stack direction="horizontal" gap="md" items="center">
                <Skeleton className="h-10 w-10 rounded-full" />
                <Stack className="flex-1" gap="xs">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                </Stack>
                <Stack items="end" gap="xs">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-3 w-24" />
                </Stack>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack>
      <Stack direction="horizontal" justify="between" items={"center"}>
        <Title size={"lg"} header={t("goals:my_goals")} />
        <Stack gap={"sm"} direction={"horizontal"}>
          <DropdownMenu
            trigger={
              <ButtonIcon
                icon={<FontAwesomeIcon icon="fa-light fa-ellipsis" />}
                variant={"secondary"}
              />
            }
          >
            <DropdownMenuItem
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-eye"} />}
              onClick={() => setShowAll(!showAll)}
              title={showAll ? t("goals:show_active_only") : t("goals:show_all")}
            />
          </DropdownMenu>
          {canEditField(employee, EmployeeFieldPermission.employee_goals) && (
            <Button
              onClick={handleCreateGoal}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            >
              {t("goals:create_goal")}
            </Button>
          )}
        </Stack>
      </Stack>

      {goals.length === 0 ? (
        <EmptyStateBase
          emoji={":dart:"}
          title={t("goals:goals_empty_state_title")}
          description={t("goals:goals_empty_state_description")}
          primaryButton={
            canEditField(employee, EmployeeFieldPermission.employee_goals) && (
              <Button onClick={handleCreateGoal}>{t("goals:create_goal")}</Button>
            )
          }
        />
      ) : (
        <Stack>
          {renderGoalsSection(publicGoals, t("goals:privacy_public"))}
          {renderGoalsSection(privateGoals, t("goals:privacy_private"), "fa-light fa-lock")}
        </Stack>
      )}
    </Stack>
  );
}
